import { useForm } from 'react-hook-form'
import selectOptions from '../../constants/selectOptions'
import { IGenericObjectStringValue } from '../../types'
import CardTAG from '../CardTAG'
import Dropdown from '../Dropdown'
import PageBodyLayout from '../PageBodyLayout'
import CaseTimelineDataPart from './CaseTimelineDataPart'
import AccordionTimeline from './AccordionTimeline'
import Chips from '../Chips'
import { useEffect, useMemo, useState } from 'react'
import { IFilterObject } from '../FilterComponent'
import { updateFilterObjectCheckedValue } from '../../pages/ExportPage'
import constants from './constants'
import utils from './utils'
import { Box, Button } from '@mui/material'
import useDebounce from '../../hooks/useDebounce'
import { useSelector } from 'react-redux'
import usersSelectors from '../../store/users/usersSelectors'
import reportsSelectors from '../../store/reports/reportsSelectors'
import eventsService from '../../services/eventsService'
import ReportSubheader from '../ReportSubheader'
import { ITimelineEvent } from './types'
import DividerTAG from '../DividerTAG'

const CaseTimeline: React.FC = () => {
  const [filter, setFilter] = useState<IFilterObject[]>(constants.defaultFilter)
  const debouncedFilterValue = useDebounce(filter, 500)
  const [selectedValues, setSelectedValues] = useState<{ label: string; value: string }[]>([])
  const [events, setEvents] = useState<ITimelineEvent[]>()
  const teamMembers = useSelector(usersSelectors.teamMembers)
  const selectedReport = useSelector(reportsSelectors.selectedReport)
  const formMethods = useForm<IGenericObjectStringValue>({
    defaultValues: { ordering: 'created_at', jumpTo: '' },
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { control, watch } = formMethods
  const orderingWatchValue = watch('ordering')

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, section: string) => {
    const response = updateFilterObjectCheckedValue(filter, [event?.target?.value])
    if (response) {
      setFilter(response)
    }

    if (!event?.target?.checked) {
      const filteredOptions = selectedValues.filter(option => option?.value !== event?.target.value)
      setSelectedValues(filteredOptions)
      return
    }
    setSelectedValues([
      ...selectedValues,
      {
        label: `${section}: ${event?.target?.id}`,
        value: event?.target?.value,
      },
    ])
  }

  const removeChipHandler = (value: string) => {
    const response = updateFilterObjectCheckedValue(filter, [value])
    if (response) {
      setFilter(response)
    }
    const filteredOptions = selectedValues.filter(option => option?.value !== value)
    setSelectedValues(filteredOptions)
  }

  const renderFilterSections = (filters: IFilterObject[]) => {
    return filters.map(filter => {
      if (filter?.section) {
        return (
          <AccordionTimeline title={filter?.title} key={filter?.value}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {utils.renderCheckboxes(filter?.subTypes ?? [], onChangeHandler, filter?.value)}
            </Box>
          </AccordionTimeline>
        )
      }
    })
  }

  const resetFilter = () => {
    setSelectedValues([])
    setFilter(constants.defaultFilter)
  }

  useEffect(() => {
    if (teamMembers && teamMembers?.length > 0) {
      const formatedTeam = teamMembers?.map(member => {
        return {
          title: `${member?.user?.first_name} ${member?.user?.last_name} `,
          value: `${member?.id}`,
          checked: false,
          section: false,
        } as IFilterObject
      })
      setFilter([
        ...filter,
        {
          title: 'Updates By Person',
          value: 'persons',
          section: true,
          checked: false,
          subTypes: formatedTeam,
        },
      ])
    }
  }, [teamMembers])

  const filterData = async (filter: any) => {
    const { data } = await eventsService.filterEvents(filter)
    const promises = data?.map(async (timelineEvent: ITimelineEvent) => {
      return await eventsService.getContent(timelineEvent.id, timelineEvent.type).then(res => {
        return { ...timelineEvent, files: res.data }
      })
    })

    Promise.all(promises).then(res => {
      setEvents(res)
    })
  }

  useEffect(() => {
    filterData({
      report: selectedReport?.id,
      ordering: orderingWatchValue,
      ...utils.getSelectedValues(debouncedFilterValue),
    })
  }, [debouncedFilterValue, orderingWatchValue])

  const sortedEvents = useMemo(() => {
    if (!events?.length) return events
    const orderDesc = orderingWatchValue === '-created_at'
    return [...events].sort((a, b) => {
      const tA = new Date(a.created_at).getTime()
      const tB = new Date(b.created_at).getTime()
      return orderDesc ? tB - tA : tA - tB
    })
  }, [events, orderingWatchValue])

  return (
    <PageBodyLayout
      className={'t-case-timeline-page'}
      caseSubheader={<ReportSubheader />}
      dataPart={<CaseTimelineDataPart events={sortedEvents} />}
      filterPart={
        <CardTAG title='Filter'>
          <div className='t-report-card-top'>
            <Dropdown
              control={control}
              label='Sort by'
              options={selectOptions.ascedingDescendingOptions}
              fieldName='ordering'
              selectedValue={orderingWatchValue}
            />
          </div>
          {selectedValues?.length > 0 ? (
            <Box className={'t-chips-outer-wrap t-vbox'}>
              <div className='t-chip-control t-vbox'>
                <span className='t-chips-label t-font-size-small t-font-bold'>Current Presets</span>
                <Chips
                  values={selectedValues}
                  handleRemove={removeChipHandler}
                  customChipStyle={constants.customChipStyle}
                />
              </div>
              <Button variant='dashed' onClick={resetFilter}>
                Clear All
              </Button>
            </Box>
          ) : null}
          <div className='t-vbox' style={{ gap: '1.8rem' }}>
            <DividerTAG />
            {renderFilterSections(filter)}
          </div>
        </CardTAG>
      }
    />
  )
}
export default CaseTimeline
